/**
 * DropFollowButton — one follow, for the whole publisher surface.
 *
 * Built to `devourplay-anchor/Drop-In/team-drops/publisher-drop.html`
 * (`.hero__follow`, line 124; `.teambar`/`.follow`, lines 571-576).
 *
 * The anchor puts a follow in two places on a publisher page — beside the
 * title in the hero, and above the creator roster — and they are the SAME
 * follow. That is the pitch the note under the roster makes: one follow, and
 * you hear when any official creator goes live, on any platform. Two different
 * subscriptions would make that sentence false.
 *
 * Signed out, this defers to the page's login handler rather than failing the
 * request: a follow button that 401s is worse than one that asks you to sign in.
 */

import React from "react";
import {useGetDropFollowStatus, useFollowDrop, useUnfollowDrop} from "@/hooks/drop/useDropFollow";
import "./DropFollowButton.scss";

interface DropFollowButtonProps {
    slug: string;
    /** Called instead of following when nobody is signed in. */
    onLoginRequired?: () => void;
    /** Referrer attribution passed through to the follow. */
    referralUserId?: string;
    /**
     * `hero` sits on the key art; `bar` sits above a roster; `icon` is the
     * compact glyph-only form used on a roster row, where a full-width labelled
     * button would outweigh the creator's own name.
     */
    variant?: "hero" | "bar" | "icon";
    className?: string;
}

export function DropFollowButton({
    slug, onLoginRequired, referralUserId, variant = "hero", className,
}: DropFollowButtonProps): React.ReactElement {
    const {data: status} = useGetDropFollowStatus(slug);
    const follow = useFollowDrop();
    const unfollow = useUnfollowDrop();

    const isFollowing = Boolean(status?.isFollowing);
    const pending = follow.isPending || unfollow.isPending;

    const onClick = () => {
        /*
         * `status` is null when signed out — the hook short-circuits without a
         * token — so an absent status is the signal to send them to login.
         */
        if (!status) {
            onLoginRequired?.();
            return;
        }
        if (isFollowing) {
            unfollow.mutate(slug);
        } else {
            follow.mutate({slug, dropRef: referralUserId});
        }
    };

    return (
        <button
            type="button"
            className={`drop-follow drop-follow--${variant}${isFollowing ? " drop-follow--on" : ""}${className ? ` ${className}` : ""}`}
            onClick={onClick}
            disabled={pending}
            aria-pressed={isFollowing}
            /* Glyph-only, so the control still has to say what it does. */
            aria-label={variant === "icon" ? isFollowing ? "Unfollow" : "Follow" : undefined}
            title={variant === "icon" ? isFollowing ? "Unfollow" : "Follow" : undefined}
        >
            {variant === "icon"
                ? isFollowing ? "✓" : "◎"
                : isFollowing ? "✓ Following" : "◎ Follow"}
        </button>
    );
}
